import { App } from "antd";
import clsx from "clsx";
import { Check, Copy } from "lucide-react";
import { useState, type ReactNode } from "react";

/** key=value chips, the way labels are written in routes and silences. */
export function Labels({ labels, max = 6, className }: { labels: Record<string, string> | null | undefined; max?: number; className?: string }) {
  const entries = Object.entries(labels ?? {});
  if (!entries.length) return <span className="text-muted">—</span>;
  return (
    <span className={clsx("inline-flex flex-wrap gap-1", className)}>
      {entries.slice(0, max).map(([k, v]) => (
        <span key={k} className="inline-flex items-center rounded bg-surface-2 px-1.5 py-0.5 font-mono text-[11px] text-navy-500">
          <span className="text-muted">{k}=</span>
          <span className="text-ink">{v}</span>
        </span>
      ))}
      {entries.length > max && <span className="text-[11px] text-muted">+{entries.length - max}</span>}
    </span>
  );
}

/** Empty states use the display face, per the design system: calm, not an error. */
export function EmptyState({ title, body, action, icon }: { title: string; body?: ReactNode; action?: ReactNode; icon?: ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center px-6 py-14 text-center">
      {icon && <div className="mb-4 text-navy-200">{icon}</div>}
      <p className="font-display text-[26px] italic leading-tight text-ink">{title}</p>
      {body && <p className="mt-2 max-w-md text-[13px] text-muted">{body}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

export function CopyButton({ text, label = "Copy" }: { text: string; label?: string }) {
  const { message } = App.useApp();
  const [done, setDone] = useState(false);
  return (
    <button
      type="button"
      className="btn-sharp btn-secondary btn-sm"
      onClick={async () => {
        await navigator.clipboard.writeText(text);
        setDone(true);
        message.success("Copied");
        setTimeout(() => setDone(false), 1500);
      }}
    >
      {done ? <Check size={14} /> : <Copy size={14} />}
      {done ? "Copied" : label}
    </button>
  );
}

export function CodeSlab({ code, copy = true, wrap = false }: { code: string; copy?: boolean; wrap?: boolean }) {
  // The copy button sits in its own bar so it never covers the end of a long line.
  return (
    <div>
      {copy && (
        <div className="flex justify-end bg-navy-800 px-2 py-1.5">
          <CopyButton text={code} />
        </div>
      )}
      {/* Single values such as URLs and tokens wrap, so all of them is visible before copying. */}
      <pre className="code-slab m-0" style={wrap ? { whiteSpace: "pre-wrap", wordBreak: "break-all" } : undefined}>
        {code}
      </pre>
    </div>
  );
}

/** A card with a header row: the page's building block. */
export function Section({ title, subtitle, actions, children, className, bodyClassName }: { title: ReactNode; subtitle?: ReactNode; actions?: ReactNode; children: ReactNode; className?: string; bodyClassName?: string }) {
  return (
    <section className={clsx("surface-card", className)}>
      <div className="card-head">
        <div className="min-w-0">
          <h2 className="m-0 font-grotesk text-[15px] font-semibold text-ink">{title}</h2>
          {subtitle && <p className="m-0 mt-0.5 text-[12px] text-muted">{subtitle}</p>}
        </div>
        {actions && <div className="flex flex-shrink-0 items-center gap-2">{actions}</div>}
      </div>
      <div className={bodyClassName}>{children}</div>
    </section>
  );
}

/** Label/value rows for detail panels. */
export function Facts({ items }: { items: { label: string; value: ReactNode }[] }) {
  return (
    <dl className="m-0 grid grid-cols-[minmax(110px,max-content)_1fr] gap-x-6 gap-y-3 px-5 py-4 text-[13px]">
      {items.map((item) => (
        <div key={item.label} className="contents">
          <dt className="text-muted">{item.label}</dt>
          <dd className="m-0 min-w-0 break-words text-ink">{item.value}</dd>
        </div>
      ))}
    </dl>
  );
}

/** Duration trend for a monitor's results: area, faint baseline, emphasised last point. */
export function Sparkline({ values, width = 260, height = 56, failed = [] }: { values: number[]; width?: number; height?: number; failed?: boolean[] }) {
  if (values.length < 2) return <div style={{ height }} className="flex items-center text-[12px] text-muted">Not enough runs yet</div>;
  const max = Math.max(...values, 1);
  const pad = 4;
  const step = (width - pad * 2) / (values.length - 1);
  const y = (v: number) => height - pad - (v / max) * (height - pad * 2);
  const points = values.map((v, i) => [pad + i * step, y(v)] as const);
  const line = points.map(([px, py], i) => `${i ? "L" : "M"}${px.toFixed(1)},${py.toFixed(1)}`).join(" ");
  const area = `${line} L${points.at(-1)![0].toFixed(1)},${height - pad} L${pad},${height - pad} Z`;
  const last = points.at(-1)!;
  return (
    <svg width="100%" viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" style={{ height }} role="img" aria-label="Response time trend">
      <line x1={pad} x2={width - pad} y1={height - pad} y2={height - pad} stroke="#EBECF3" />
      <path d={area} fill="#EEEFF5" />
      <path d={line} fill="none" stroke="#3A4078" strokeWidth={1.5} vectorEffect="non-scaling-stroke" />
      {points.map(([px, py], i) => (failed[i] ? <circle key={i} cx={px} cy={py} r={2.5} fill="#C0443E" /> : null))}
      <circle cx={last[0]} cy={last[1]} r={3.5} fill={failed.at(-1) ? "#C0443E" : "#0D1245"} />
    </svg>
  );
}

export function Muted({ children }: { children: ReactNode }) {
  return <span className="text-muted">{children}</span>;
}
